import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Car, Home, AlertTriangle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ObjectTypeSection from './ObjectTypeSection';
import CarParametersSection from './CarParametersSection';
import PropertyParametersSection from './PropertyParametersSection';
import TaxPrivilegesSection from './TaxPrivilegesSection';
import LocationSection from './LocationSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { calculateLuxuryTax } from '../../../../../utils/calculations/taxCalculations';

/**
 * Компонент основной формы расчета налога на роскошь
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setTaxDetails,
  taxDetails,
  carTypes,
  propertyTypes,
  privilegeTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновление связанных полей при изменении типа объекта
      if (name === 'taxObjectType') {
        // Сбрасываем некоторые поля при переключении типа объекта
      }

      // Обновление связанных полей при изменении типа автомобиля
      if (name === 'carType') {
        // Логика для автоматического изменения коэффициентов и т.д.
      }

      // Обновление связанных полей при изменении типа недвижимости
      if (name === 'propertyType') {
        // Логика для автоматического изменения ставок и т.д.
      }

      // Обновление кадастровой стоимости при изменении стоимости недвижимости
      if (name === 'propertyCost' && prev.cadastralValue === prev.propertyCost) {
        // Обычно кадастровая стоимость составляет около 80% от рыночной
        const numPropertyCost = parseFloatOrDefault(value, 0);
        newState.cadastralValue = Math.round(numPropertyCost * 0.8).toString();
      }

      // Логика для других зависимых полей
      if (name === 'hasPrivileges' && !checked) {
        newState.privilegeType = 'none';
      }

      if (name === 'hasChildrenShare' && !checked) {
        newState.childrenSharePercent = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      taxObjectType, year,
      carCost, carPower, carAge, carType,
      propertyCost, propertyType, propertyArea, cadastralValue, ownershipShare,
      hasPrivileges, privilegeType,
      hasChildrenShare, childrenSharePercent,
      region
    } = formData;

    // Парсинг числовых значений
    const numYear = parseIntOrDefault(year, -1);
    const currentYear = new Date().getFullYear();

    // Общая валидация
    if (numYear < 2000 || numYear > currentYear + 1) {
      newErrors.year = `Год должен быть между 2000 и ${currentYear + 1}.`;
    }

    if (!region) {
      newErrors.region = 'Выберите регион.';
    }

    // Валидация для автомобилей
    if (taxObjectType === 'car') {
      const numCarCost = parseFloatOrDefault(carCost, -1);
      const numCarPower = parseFloatOrDefault(carPower, -1);
      const numCarAge = parseIntOrDefault(carAge, -1);

      if (numCarCost <= 0) {
        newErrors.carCost = 'Стоимость должна быть положительным числом.';
      } else if (numCarCost > 300000000) {
        newErrors.carCost = 'Максимальная стоимость 300 млн ₽.';
      }

      if (numCarPower <= 0) {
        newErrors.carPower = 'Мощность должна быть положительным числом.';
      } else if (numCarPower > 1500) {
        newErrors.carPower = 'Максимальная мощность 1500 л.с.';
      }

      if (numCarAge < 0) {
        newErrors.carAge = 'Возраст не может быть отрицательным.';
      } else if (numCarAge > 100) {
        newErrors.carAge = 'Максимальный возраст 100 лет.';
      }

      if (!carType) {
        newErrors.carType = 'Выберите тип автомобиля.';
      }
    }

    // Валидация для недвижимости
    if (taxObjectType === 'property') {
      const numPropertyCost = parseFloatOrDefault(propertyCost, -1);
      const numPropertyArea = parseFloatOrDefault(propertyArea, -1);
      const numCadastralValue = parseFloatOrDefault(cadastralValue, -1);
      const numOwnershipShare = parseFloatOrDefault(ownershipShare, -1);

      if (numPropertyCost <= 0) {
        newErrors.propertyCost = 'Стоимость должна быть положительным числом.';
      } else if (numPropertyCost > 10000000000) {
        newErrors.propertyCost = 'Максимальная стоимость 10 млрд ₽.';
      }

      if (numPropertyArea <= 0) {
        newErrors.propertyArea = 'Площадь должна быть положительным числом.';
      } else if (numPropertyArea > 10000) {
        newErrors.propertyArea = 'Максимальная площадь 10000 кв.м.';
      }

      if (numCadastralValue <= 0) {
        newErrors.cadastralValue = 'Кадастровая стоимость должна быть положительным числом.';
      } else if (numCadastralValue > numPropertyCost * 1.5) {
        newErrors.cadastralValue = 'Кадастровая стоимость не может превышать рыночную более чем в 1.5 раза.';
      }

      if (numOwnershipShare <= 0 || numOwnershipShare > 100) {
        newErrors.ownershipShare = 'Доля должна быть от 1 до 100%.';
      }

      if (!propertyType) {
        newErrors.propertyType = 'Выберите тип недвижимости.';
      }
    }

    // Валидация для налоговых льгот
    if (hasPrivileges && (!privilegeType || privilegeType === 'none')) {
      newErrors.privilegeType = 'Выберите тип льготы.';
    }

    if (hasChildrenShare) {
      const numChildrenShare = parseFloatOrDefault(childrenSharePercent, -1);
      if (numChildrenShare <= 0 || numChildrenShare > 100) {
        newErrors.childrenSharePercent = 'Доля детей должна быть от 1 до 100%.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета налога на роскошь
// Функция расчета налога на роскошь
const calculatTax = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors || {})[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
  
    setIsCalculating(true);
    setResults(null);
    setTaxDetails([]);  // Убедитесь, что используется setTaxDetails, а не taxDetails
  
    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // Создаем заглушку для функции расчета, пока нет реальной реализации
        // Это позволит избежать ошибок, связанных с отсутствием файла taxCalculations
        
        // Создаем имитацию результатов расчета для тестирования
        const mockResults = {
          results: {
            baseTax: 15000,
            totalTax: 30000,
            taxPerHorsePower: 20,
            luxuryCoefficient: 2,
            registrationCoefficient: 1,
            electricCarCoefficient: 1,
            privilegeDiscount: 0,
            region: formData.region,
            year: parseInt(formData.year),
            isLuxury: formData.carType === 'luxury',
            carPower: parseFloat(formData.carPower),
            carCost: parseFloat(formData.carCost),
            carAge: parseInt(formData.carAge)
          },
          details: [
            {
              name: 'Мощность двигателя',
              value: `${formData.carPower} л.с.`,
              coefficient: null,
              impact: null,
              description: 'Базовый параметр для расчета транспортного налога'
            },
            {
              name: 'Базовая ставка налога',
              value: `20 ₽/л.с.`,
              coefficient: null,
              impact: 20 * parseFloat(formData.carPower),
              description: 'Ставка за 1 л.с., зависит от региона и мощности'
            },
            {
              name: 'Повышающий коэффициент (налог на роскошь)',
              value: `Автомобиль стоимостью ${parseFloat(formData.carCost).toLocaleString('ru-RU')} ₽`,
              coefficient: 2,
              impact: 15000,
              description: 'Увеличение налога для дорогих автомобилей'
            }
          ]
        };
  
        setResults(mockResults.results);
        setTaxDetails(mockResults.details);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setTaxDetails([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setTaxDetails, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    const isCarType = formData.taxObjectType === 'car';
    setFormData({
      // Общие параметры
      taxObjectType: isCarType ? 'car' : 'property',
      year: new Date().getFullYear().toString(),
      
      // Параметры для автомобилей
      carCost: '3000000',
      carPower: '150',
      carAge: '0',
      carType: 'standard',
      isElectric: false,
      isFirstRegistration: true,
      
      // Параметры для недвижимости
      propertyCost: '10000000',
      propertyType: 'apartment',
      propertyArea: '80',
      cadastralValue: '8000000',
      ownershipType: 'individual',
      ownershipShare: '100',
      isOnlyProperty: true,
      
      // Параметры налоговых льгот
      hasPrivileges: false,
      privilegeType: 'none',
      hasChildrenShare: false,
      childrenSharePercent: '0',
      
      // Местоположение
      region: 'moscow',
      municipality: 'central',
    });
    setResults(null);
    setTaxDetails([]);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов расчета в CSV
  const exportToCSV = () => {
    const localTaxDetails = taxDetails || [];  // Используем локальную переменную с проверкой
    
    if (!results || localTaxDetails.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Параметр', 'Значение', 'Коэффициент', 'Влияние на налог'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = setTaxDetails.map(item => {
      return [
        item.name,
        item.value,
        item.coefficient ? formatCsvNumber(item.coefficient) : '',
        item.impact ? formatCsvNumber(item.impact) : '0,00'
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      'ИТОГО:', 
      '', 
      '',
      formatCsvNumber(results.totalTax)
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Тип объекта:', formData.taxObjectType === 'car' ? 'Автомобиль' : 'Недвижимость'],
      ['Год расчета:', formData.year],
      formData.taxObjectType === 'car' 
        ? ['Стоимость автомобиля:', `${formatCsvNumber(parseFloatOrDefault(formData.carCost))} ₽`]
        : ['Стоимость недвижимости:', `${formatCsvNumber(parseFloatOrDefault(formData.propertyCost))} ₽`],
      formData.taxObjectType === 'car'
        ? ['Мощность двигателя:', `${formData.carPower} л.с.`]
        : ['Кадастровая стоимость:', `${formatCsvNumber(parseFloatOrDefault(formData.cadastralValue))} ₽`],
      ['Регион:', formData.region === 'moscow' ? 'Москва' : formData.region],
      ['Налоговые льготы:', formData.hasPrivileges ? 'Да' : 'Нет'],
      ['Сумма налога:', `${formatCsvNumber(results.totalTax)} ₽`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_налога_на_роскошь.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatTax();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры для расчета налога
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Информационное сообщение о налоге на роскошь */}
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">Важная информация о налоге на роскошь</h3>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
              В России налог на роскошь применяется к автомобилям стоимостью от 3 млн рублей и к недвижимости с кадастровой 
              стоимостью от 300 млн рублей (для жилых помещений). Ставки и условия различаются в зависимости от региона и типа имущества.
            </p>
          </div>
        </div>

        {/* Выбор объекта налогообложения (автомобиль или недвижимость) */}
        <ObjectTypeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры автомобиля */}
        {formData.taxObjectType === 'car' && (
          <CarParametersSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            carTypes={carTypes}
          />
        )}

        {/* Параметры недвижимости */}
        {formData.taxObjectType === 'property' && (
          <PropertyParametersSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            propertyTypes={propertyTypes}
          />
        )}

        {/* Налоговые льготы */}
        <TaxPrivilegesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          privilegeTypes={privilegeTypes}
        />

        {/* Местоположение (регион) */}
        <LocationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать налог'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && taxDetails.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт в CSV
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;